// Логическата геометрия на схемата. Позициите са в ЛОГИЧЕСКИ единици спрямо
// региона (1 единица = 1 см), не в пиксели — платното се мащабира до
// наличното място, затова една и съща схема стои еднакво на монитор, таблет и
// телефон. REGION_DIMENSIONS е договор с BE (`restaurant_schema/geometry.py`).
// Формата не се избира: новите региони са `rectangle`, останалите форми са на
// заварени региони. Регионът няма видима граница и НЕ ограничава елементите —
// те стоят навсякъде (и на отрицателни координати); размерът му е само
// началната област (празен регион, автоматичната подредба).
//
// Размерите на масите/обектите идват от дизайна („Схема на заведението —
// подобрения", Claude Design) в неговите пиксели × DESIGN_UNIT: там регионът е
// 960px, тук 1600 единици — пропорциите остават едни и същи.

import type {
  ElementSize,
  ObjectKind,
  RegionShape,
  SchemaElement,
  TableShape,
} from "../../api/restaurantSchema";

export const REGION_DIMENSIONS: Record<
  RegionShape,
  { width: number; height: number }
> = {
  square: { width: 1200, height: 1200 },
  rectangle: { width: 1600, height: 1200 },
  wide: { width: 2000, height: 1000 },
  long: { width: 1000, height: 2000 },
};

/** Логически единици в един пиксел на дизайна. */
export const DESIGN_UNIT = 1.6;
/** Стъпката на мрежата — влаченето и автоматичната подредба са на нея. */
export const GRID_STEP = 10;
export const MAX_SEATS = 40;

/**
 * Колко навътре от ръба на региона подрежда масите автоматичната подредба
 * („Конфигурация на маси", 6px в дизайна).
 */
export const REGION_EDGE_INSET = 6 * DESIGN_UNIT;
/**
 * Столът: 22×10px, центърът му е на 10px от ръба на плота. По-тънък от
 * дизайна (22×14 на 13px) — масите се събират по-плътно.
 */
export const CHAIR_WIDTH = 22 * DESIGN_UNIT;
export const CHAIR_DEPTH = 10 * DESIGN_UNIT;
const CHAIR_OFFSET = 10 * DESIGN_UNIT;
/** Колко стърчи столът извън ръба на плота (външният му ръб). */
export const CHAIR_REACH = CHAIR_OFFSET + CHAIR_DEPTH / 2;
/**
 * Най-малкото отстояние между масите (със столовете) — 10 см, една стъпка на
 * мрежата. Пазят го свободното място за нов елемент (`findFreeSpot`) и
 * предпазителят срещу застъпване (`tableCollision.ts`).
 */
export const MIN_TABLE_GAP = GRID_STEP;

/**
 * Дължината на стена, разпъната с дръжките (логически единици = см). Договор
 * с BE (`WALL_LENGTH_MIN/MAX` в `restaurant_schema/geometry.py`).
 */
export const WALL_LENGTH_MIN = 20;
export const WALL_LENGTH_MAX = 5000;

// Обектите: множител върху базовия им размер (избира се в инспектора).
const OBJECT_SCALE: Record<ElementSize, number> = {
  small: 0.75,
  medium: 1,
  large: 1.3,
  xl: 1.6,
};

// Базовите размери от дизайна, в пиксели на дизайна. Входът е врата: отворът
// по ширина и колкото половината му навътре — там се отварят двете крила
// (`EntranceDoor`).
const OBJECT_BASE: Record<ObjectKind, { width: number; height: number }> = {
  bar: { width: 320, height: 76 },
  wall: { width: 170, height: 6 },
  plant: { width: 44, height: 44 },
  entrance: { width: 120, height: 60 },
  stage: { width: 210, height: 116 },
  dj: { width: 90, height: 64 },
};

export interface Chair {
  // Центърът на стола спрямо горния ляв ъгъл на плота + ъгъл на завъртане.
  x: number;
  y: number;
  angle: number;
}

// Масата: множител на плота по избрания размер (Малка…XL). „Средна" е малко под
// размера от дизайна — там масите излизат едри за реална зала.
const TABLE_SCALE: Record<ElementSize, number> = {
  small: 0.65,
  medium: 0.85,
  large: 1.05,
  xl: 1.25,
};

/**
 * Плотът расте с броя места (формулата от дизайна), а размерът (Малка…XL)
 * го смалява/уголемява. Столовете остават с един и същи размер.
 */
export const tableDimensions = (shape: TableShape, seats: number, size: ElementSize) => {
  const scale = TABLE_SCALE[size] * DESIGN_UNIT;
  if (shape === "round") {
    const diameter = Math.max(64, 56 + seats * 5) * scale;
    return { width: diameter, height: diameter };
  }
  if (shape === "square") {
    const side = Math.max(72, 64 + seats * 4) * scale;
    return { width: side, height: side };
  }
  return {
    width: Math.max(110, 40 + Math.ceil(seats / 2) * 34) * scale,
    height: 72 * scale,
  };
};

/**
 * Столовете около плота: по окръжност за кръгла маса, по страните за другите.
 * Маса за един има един стол — отгоре.
 */
export const tableChairs = (
  shape: TableShape,
  seats: number,
  width: number,
  height: number,
): Chair[] => {
  const chairs: Chair[] = [];
  if (shape === "round") {
    const radius = width / 2 + CHAIR_OFFSET;
    for (let index = 0; index < seats; index++) {
      const radians = (index / seats) * Math.PI * 2 - Math.PI / 2;
      chairs.push({
        x: width / 2 + radius * Math.cos(radians),
        y: height / 2 + radius * Math.sin(radians),
        angle: (radians * 180) / Math.PI + 90,
      });
    }
    return chairs;
  }
  // Брой столове по страна: горе, дясно, долу, ляво.
  let perSide: number[];
  if (shape === "square") {
    const base = Math.floor(seats / 4);
    const rest = seats % 4;
    perSide = [
      base + (rest > 0 ? 1 : 0),
      base + (rest > 2 ? 1 : 0),
      base + (rest > 1 ? 1 : 0),
      base,
    ];
  } else {
    perSide = [Math.ceil(seats / 2), 0, Math.floor(seats / 2), 0];
  }
  const spread = (count: number, length: number, place: (at: number) => void) => {
    for (let index = 0; index < count; index++) place((length * (index + 1)) / (count + 1));
  };
  spread(perSide[0], width, (x) => chairs.push({ x, y: -CHAIR_OFFSET, angle: 0 }));
  spread(perSide[1], height, (y) => chairs.push({ x: width + CHAIR_OFFSET, y, angle: 90 }));
  spread(perSide[2], width, (x) => chairs.push({ x, y: height + CHAIR_OFFSET, angle: 0 }));
  spread(perSide[3], height, (y) => chairs.push({ x: -CHAIR_OFFSET, y, angle: 90 }));
  return chairs;
};

/**
 * Размерът на обекта. Стената е дълга `length` (разпъната с дръжките); без
 * него (стена отпреди полето) — по `size`. Стената става по-дълга, не по-дебела.
 */
export const objectDimensions = (kind: ObjectKind, size: ElementSize, length: number | null = null) => {
  const base = OBJECT_BASE[kind];
  const scale = OBJECT_SCALE[size];
  if (kind === "wall") {
    return {
      width: length ?? base.width * scale * DESIGN_UNIT,
      height: base.height * DESIGN_UNIT,
    };
  }
  return {
    width: base.width * scale * DESIGN_UNIT,
    height: base.height * scale * DESIGN_UNIT,
  };
};

/** Дължината на стената в цели см — записаната или тази по `size`. */
export const wallLength = (element: SchemaElement) =>
  Math.round(objectDimensions("wall", element.size, element.length).width);

/** Размерът на плота/обекта без столовете. */
export const elementDimensions = (element: SchemaElement) =>
  element.kind === "table"
    ? tableDimensions(element.tableShape ?? "round", element.seats ?? 4, element.size)
    : objectDimensions(element.kind as ObjectKind, element.size, element.length);

/**
 * Половината от ширината/височината на завъртения елемент в екранни оси — с
 * външния ред столове (заетото място) или без тях (`withChairs: false` —
 * само плотът; по него се подравнява при местене).
 */
export const elementHalfExtents = (element: SchemaElement, withChairs = true) => {
  const { width, height } = elementDimensions(element);
  const chairs = withChairs && element.kind === "table" ? CHAIR_REACH * 2 : 0;
  const radians = (element.rotation * Math.PI) / 180;
  const cos = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));
  const fullWidth = width + chairs;
  const fullHeight = height + chairs;
  return {
    halfWidth: (fullWidth * cos + fullHeight * sin) / 2,
    halfHeight: (fullWidth * sin + fullHeight * cos) / 2,
  };
};

export const snapToGrid = (value: number) =>
  Math.round(value / GRID_STEP) * GRID_STEP;

/** Правоъгълник в логически единици. */
export interface LogicalBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * Рамката около всички елементи (завъртени, със столовете) — това вписва
 * „Побери схемата". Елементите не са ограничени до региона: могат да стоят
 * навсякъде, вкл. на отрицателни координати. Празен регион → null.
 */
export const contentBounds = (elements: SchemaElement[]): LogicalBounds | null => {
  if (elements.length === 0) return null;
  const bounds = { left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity };
  for (const element of elements) {
    const { halfWidth, halfHeight } = elementHalfExtents(element);
    bounds.left = Math.min(bounds.left, element.x - halfWidth);
    bounds.top = Math.min(bounds.top, element.y - halfHeight);
    bounds.right = Math.max(bounds.right, element.x + halfWidth);
    bounds.bottom = Math.max(bounds.bottom, element.y + halfHeight);
  }
  return bounds;
};

// Отстоянието между новия елемент и съседите му (със столовете) — като между масите.
const FREE_SPOT_GAP = MIN_TABLE_GAP;
// Стъпката на спиралата при търсене на свободно място и колко навън стига.
const FREE_SPOT_STEP = 80;
const FREE_SPOT_RINGS = 28;
// Размерът по подразбиране, когато не е подаден новият елемент.
const DEFAULT_FREE_SPOT_HALF = 75;

/**
 * Свободно място за нов елемент: `origin` (редакторът подава средата на
 * видимото платно; по подразбиране — центърът на региона), а ако е зает —
 * спирала навън, докато не се намери място, където новият елемент НЕ
 * застъпва нито един от съществуващите. Застъпването се мери с истинските
 * размери (завъртени, със столовете) на двете страни плюс FREE_SPOT_GAP —
 * иначе широк бар/сцена/XL маса покрива съседа си. Копието на елемент търси
 * до оригинала. Няма свободно място → `origin`.
 */
export const findFreeSpot = (
  shape: RegionShape,
  elements: SchemaElement[],
  origin?: { x: number; y: number },
  newElement?: SchemaElement,
): { x: number; y: number } => {
  const { width, height } = REGION_DIMENSIONS[shape];
  const size = newElement
    ? elementHalfExtents(newElement)
    : { halfWidth: DEFAULT_FREE_SPOT_HALF, halfHeight: DEFAULT_FREE_SPOT_HALF };
  const occupied = elements.map((element) => ({ element, ...elementHalfExtents(element) }));
  const start = origin ?? { x: width / 2, y: height / 2 };
  const center = { x: snapToGrid(start.x), y: snapToGrid(start.y) };
  const isTaken = (x: number, y: number) =>
    occupied.some(
      ({ element, halfWidth, halfHeight }) =>
        Math.abs(element.x - x) < halfWidth + size.halfWidth + FREE_SPOT_GAP &&
        Math.abs(element.y - y) < halfHeight + size.halfHeight + FREE_SPOT_GAP,
    );
  if (!isTaken(center.x, center.y)) return center;
  for (let ring = 1; ring <= FREE_SPOT_RINGS; ring++) {
    for (let step = 0; step < ring * 8; step++) {
      const angle = (2 * Math.PI * step) / (ring * 8);
      const candidate = {
        x: snapToGrid(center.x + Math.cos(angle) * ring * FREE_SPOT_STEP),
        y: snapToGrid(center.y + Math.sin(angle) * ring * FREE_SPOT_STEP),
      };
      if (!isTaken(candidate.x, candidate.y)) return candidate;
    }
  }
  return center;
};

/**
 * Масата, завъртяна на `rotation` градуса, в ЕКРАННИ оси (логически единици):
 * половин ширина/височина на плота и колко стига нагоре с най-горния стол —
 * там стоят чипът „следваща резервация" и червената точка (не се въртят).
 */
export const tableScreenExtents = (
  shape: TableShape,
  seats: number,
  size: ElementSize,
  rotation: number,
) => {
  const { width, height } = tableDimensions(shape, seats, size);
  const radians = (rotation * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const plateHalfWidth =
    shape === "round" ? width / 2 : (width * Math.abs(cos) + height * Math.abs(sin)) / 2;
  const plateHalfHeight =
    shape === "round" ? height / 2 : (width * Math.abs(sin) + height * Math.abs(cos)) / 2;
  const chairReach = tableChairs(shape, seats, width, height).map((chair) => {
    const localX = chair.x - width / 2;
    const localY = chair.y - height / 2;
    // CSS rotate() е по часовника: y' = x·sin + y·cos.
    const screenY = localX * sin + localY * cos;
    const chairRadians = ((chair.angle + rotation) * Math.PI) / 180;
    const chairHalfHeight =
      (CHAIR_WIDTH * Math.abs(Math.sin(chairRadians)) +
        CHAIR_DEPTH * Math.abs(Math.cos(chairRadians))) /
      2;
    return chairHalfHeight - screenY;
  });
  return {
    plateHalfWidth,
    plateHalfHeight,
    topReach: Math.max(plateHalfHeight, ...chairReach),
  };
};
